import axios from 'axios';

// Aggiungi questa costante all'inizio del file, dopo le importazioni
const API_BASE_URL = 'http://localhost:8001/api';
const API_URL = process.env.REACT_APP_API_URL || API_BASE_URL;
console.log('Using API URL:', API_URL);

// Crea un'istanza axios con configurazione comune
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    // Aggiungi timeout e withCredentials per gestire meglio le richieste
    timeout: 10000,
    withCredentials: false // Cambia a true se il backend supporta i cookie cross-origin
});

// Aggiungi un interceptor per gestire l'autenticazione
const SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 ore

export const checkSessionExpired = () => {
  const loginTime = localStorage.getItem('login_time');
  if (!loginTime) return false;
  return Date.now() - parseInt(loginTime, 10) > SESSION_DURATION;
};

export const clearSession = () => {
  ['token','user','user_type','user_id','company_id','login_time'].forEach(k => localStorage.removeItem(k));
};

api.interceptors.request.use(
  config => {
    const isLoginEndpoint = config.url && config.url.includes('/login/');
    const token = localStorage.getItem('token');
    if (token && !isLoginEndpoint) {
      if (checkSessionExpired()) {
        clearSession();
        window.dispatchEvent(new CustomEvent('session-expired'));
        return Promise.reject(new Error('Sessione scaduta'));
      }
      config.headers['Authorization'] = `Token ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Aggiungi un interceptor per gestire gli errori di risposta
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !error.config?.url?.includes('login')) {
      clearSession();
      window.dispatchEvent(new CustomEvent('session-expired'));
    }
    return Promise.reject(error);
  }
);

// Funzione per la registrazione di un nuovo utente
export const register = async (formData) => {
  try {
    // Log dettagliato per debug
    console.log("Registering user with FormData:");
    if (formData instanceof FormData) {
      for (let pair of formData.entries()) {
        const value = pair[1];
        if (value instanceof File) {
          console.log(`${pair[0]}: File - ${value.name} (${value.type}, ${value.size} bytes)`);
        } else {
          console.log(`${pair[0]}: ${value}`);
        }
      }
    }

    // Assicurati di usare l'endpoint corretto e le intestazioni corrette
    const response = await api.post('/users/register/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Registration response:', response.data);
    
    // Salva il token e i dati utente nel localStorage
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('login_time', Date.now().toString());
      
      // Salva anche i dati dell'utente
      const userData = {
        id: response.data.user?.id,
        username: response.data.user?.username,
        email: response.data.user?.email,
        avatar: response.data.user?.avatar
      };
      
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('user_type', 'user');
      if (response.data.user?.id) {
        localStorage.setItem('user_id', response.data.user.id);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error registering user:', error);
    
    // Log dettagliato dell'errore per il debug
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

// Ritorna [{date: 'YYYY-MM-DD', count: N}] — query leggera senza caricare gli eventi
export const getAvailableDates = async (filters = {}) => {
  const response = await api.get('/public/events/dates/', { params: filters });
  return response.data;
};

// Modifica la funzione getEvents per utilizzare l'endpoint pubblico
export const getEvents = async (filters = {}) => {
  try {
    // Log per debug
    console.log('Chiamata getEvents con filtri:', filters);
    
    // Modifica l'endpoint per utilizzare quello pubblico corretto
    const response = await api.get('/public/events/', { params: filters });
    let events = response.data;
    
    console.log('Eventi ricevuti dal server:', events);
    
    // Debug: verifica quali eventi hanno coordinate valide
    events.forEach(event => {
      console.log(`Evento ${event.id} - ${event.title}: lat=${event.latitude}, lng=${event.longitude}`);
    });
    
    // Se abbiamo la posizione dell'utente, calcoliamo le distanze ma non filtriamo ancora
    const userLocationString = localStorage.getItem('userLocation');
    if (userLocationString) {
      try {
        const userLocation = JSON.parse(userLocationString);
        console.log('Posizione utente trovata nel localStorage:', userLocation);
        
        // Aggiungi la distanza a tutti gli eventi che hanno coordinate
        events = events.map(event => {
          // Verifica che l'evento abbia coordinate valide
          if (
            event.latitude !== null && 
            event.longitude !== null && 
            event.latitude !== undefined && 
            event.longitude !== undefined
          ) {
            // Converti le coordinate in numeri se sono stringhe
            const eventLat = parseFloat(event.latitude);
            const eventLng = parseFloat(event.longitude);
            
            if (!isNaN(eventLat) && !isNaN(eventLng)) {
              // Calcola la distanza
              const distance = calculateDistance(
                userLocation.latitude, 
                userLocation.longitude, 
                eventLat, 
                eventLng
              );
              
              // Aggiungi la distanza all'evento
              event.distance = Math.round(distance * 10) / 10; // Arrotonda a 1 decimale
              console.log(`Evento ${event.id} - ${event.title} a ${event.distance}km dalla posizione utente`);
            } else {
              console.log(`Evento ${event.id} - ${event.title} ha coordinate non valide: ${event.latitude}, ${event.longitude}`);
              event.distance = null;
            }
          } else {
            console.log(`Evento ${event.id} - ${event.title} non ha coordinate`);
            event.distance = null;
          }
          
          return event;
        });
        
        // Ordina gli eventi per distanza (più vicini prima), mettendo quelli senza distanza alla fine
        events.sort((a, b) => {
          if (a.distance === null && b.distance === null) return 0;
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        });
      } catch (e) {
        console.error('Errore nel parsing della posizione utente:', e);
      }
    }
    
    console.log('Eventi con distanze calcolate:', events);
    return events;
  } catch (error) {
    console.error('Error fetching events:', error);
    // Log dettagliato dell'errore
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    throw error;
  }
};

export const getCategories = async () => {
    try {
        // Rimuovi il prefisso '/api' poiché è già incluso in API_BASE_URL
        const response = await api.get('/categories/');
        console.log('Categories response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching categories:', error);
        throw error;
    }
};

// Funzione per gestire il login
export const login = async (username, password, isCompany = false) => {
  try {
    // Modifica l'endpoint per corrispondere a quello definito nel backend
    const endpoint = isCompany ? '/company/login/' : '/users/login/';
    console.log(`Tentativo di login su endpoint: ${endpoint}`);
    
    const response = await api.post(endpoint, { username, password });
    
    console.log('Risposta login:', response.data);
    
    // Salva il token nel localStorage
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('login_time', Date.now().toString());
    
    // Salva il tipo di utente
    if (isCompany && response.data.is_company) {
      localStorage.setItem('user_type', 'company');
      if (response.data.company_id) {
        localStorage.setItem('company_id', response.data.company_id);
      }
    } else {
      localStorage.setItem('user_type', 'user');
    }
    
    // Salva l'ID utente
    if (response.data.user_id) {
      localStorage.setItem('user_id', response.data.user_id);
    }
    
    // Salva i dati dell'utente
    const userData = {
      id: response.data.user_id,
      username: username,
      email: response.data.email || '',
      avatar: response.data.avatar || null,
      is_company: response.data.is_company || false
    };
    
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Recupera i dati completi dell'utente, incluso l'avatar
    if (!isCompany) {
      try {
        await getUserProfile();
        // I dati dell'utente sono già stati salvati nel localStorage dalla funzione getUserProfile
      } catch (profileError) {
        console.error('Errore nel recupero del profilo utente dopo il login:', profileError);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Errore durante il login:', error);
    console.error('Dettagli errore:', error.response?.data || error.message);
    throw error;
  }
};

export const logout = () => {
  // Rimuovi tutti i dati dell'utente dal localStorage
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('user_type');
  localStorage.removeItem('user_id');
  localStorage.removeItem('company_id');
  
  // Puoi anche fare una chiamata al backend per invalidare il token, se necessario
  // api.post('/auth/logout/').catch(err => console.error('Errore durante il logout:', err));
  
  console.log('Utente disconnesso');
};

// Funzione per verificare se l'utente è autenticato
export const isAuthenticated = () => {
  return !!localStorage.getItem('token');
};

// Funzione per ottenere l'utente corrente
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  
  try {
    return JSON.parse(userStr);
  } catch (e) {
    console.error('Errore nel parsing dei dati utente:', e);
    return null;
  }
};

// Funzione per la registrazione di un'azienda
export const registerCompany = async (companyData) => {
  try {
    console.log('Registering company with data:', companyData);
    
    // Assicurati che i nomi dei campi corrispondano a quelli attesi dal backend
    const formattedData = {
      email: companyData.email,
      password: companyData.password,
      companyName: companyData.companyName,
      vatNumber: companyData.vatNumber,
      address: companyData.address || '',
      phone: companyData.phone || '',
      description: companyData.description || '',
      username: companyData.username || companyData.email.split('@')[0]
    };
    
    const response = await api.post('/company/register/', formattedData);
    console.log('Company registration response:', response.data);
    
    // Salva il token e i dati utente nel localStorage se necessario
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user_type', 'company');
      
      // Salva anche i dati dell'azienda
      const companyUserData = {
        id: response.data.user?.id,
        username: response.data.user?.username,
        email: response.data.user?.email,
        companyName: response.data.company?.company_name
      };
      
      localStorage.setItem('user', JSON.stringify(companyUserData));
      if (response.data.user?.id) {
        localStorage.setItem('user_id', response.data.user.id);
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error registering company:', error);
    
    // Log dettagliato dell'errore per il debug
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      console.error('Error request:', error.request);
    } else {
      console.error('Error message:', error.message);
    }
    
    throw error;
  }
};

// Funzioni API per gli eventi
export const createEvent = async (eventData) => {
  try {
    // Assicurati che l'indirizzo sia formattato correttamente per la geolocalizzazione
    if (eventData.get('location')) {
      const location = eventData.get('location');
      console.log(`Invio evento con indirizzo per geolocalizzazione: ${location}`);
      
      // Verifica che l'indirizzo sia completo e ben formattato
      if (!location.includes(',')) {
        console.warn('L indirizzo potrebbe non essere completo. Per una migliore geolocalizzazione, includere via, numero civico, citt&#xE0; e paese.');
      }
    }
    
    // Usa l'istanza api che hai gi&#xE0; configurato
    const response = await api.post('/company/events/', eventData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    console.log('Risposta creazione evento:', response.data);
    
    // Verifica se l'evento ha coordinate
    if (response.data.latitude && response.data.longitude) {
      console.log(`Evento geolocalizzato con successo: Lat ${response.data.latitude}, Lng ${response.data.longitude}`);
    } else {
      console.warn('Evento creato ma non geolocalizzato');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error creating event:', error.response?.data || error);
    throw error;
  }
};

// Per updateEvent, aggiorna anche questo:
export const updateEvent = async (eventId, eventData) => {
  try {
    const response = await api.put(`/company/events/${eventId}/`, eventData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

// Per deleteEvent, aggiorna anche questo:
export const deleteEvent = async (eventId) => {
  try {
    const response = await api.delete(`/company/events/${eventId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

// Funzione per ottenere gli eventi dell'azienda corrente
export const getCompanyEvents = async () => {
  try {
    // Assicurati che la richiesta includa il token di autenticazione
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Utente non autenticato');
    }

    console.log('Richiesta eventi azienda...');
    const response = await api.get('/company/events/', {
      headers: {
        'Authorization': `Token ${token}`
      }
    });

    console.log('Risposta eventi azienda:', response.data);
    return response.data;
  } catch (error) {
    console.error('Errore nel recupero degli eventi azienda:', error);
    // Log dettagliato dell'errore
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }
    throw error;
  }
};

// Funzione per verificare se l'utente è un'azienda
export const isCompany = () => {
  // Controlla solo il localStorage per una risposta immediata
  return localStorage.getItem('user_type') === 'company';
};

// La funzione isCompanyUser fa una verifica pi&#xF9; approfondita, inclusa una chiamata API se necessario
export const isCompanyUser = async () => {
  try {
    // Verifica se l'utente &#xE8; autenticato
    if (!isAuthenticated()) {
      return false;
    }
    
    // Verifica se l'utente &#xE8; gi&#xE0; identificato come azienda nel localStorage
    const userType = localStorage.getItem('user_type');
    if (userType === 'company') {
      return true;
    } else if (userType === 'user') {
      return false;
    }
    
    // Solo se non abbiamo informazioni nel localStorage, fai una chiamata API
    try {
      const response = await api.get('/user/is-company/');
      
      // Se la risposta &#xE8; positiva, salva l'informazione nel localStorage per future verifiche
      if (response.data && response.data.is_company) {
        localStorage.setItem('user_type', 'company');
        if (response.data.company_id) {
          localStorage.setItem('company_id', response.data.company_id);
        }
        return true;
      } else {
        // Se non &#xE8; un'azienda, salva anche questa informazione
        localStorage.setItem('user_type', 'user');
        return false;
      }
    } catch (apiError) {
      console.error('API error in isCompanyUser:', apiError);
      // In caso di errore API, non aggiorniamo il localStorage
      return false;
    }
  } catch (error) {
    console.error('Error checking company status:', error);
    // In caso di errore, assumiamo che l'utente non sia un'azienda
    return false;
  }
};

// Aggiungi questa funzione per ottenere tutte le aziende (per l'admin)
export const getAllCompanies = async () => {
  try {
    // Verifica se l'utente &#xE8; autenticato e ha i permessi di admin
    if (!isAuthenticated()) {
      throw new Error('Utente non autenticato');
    }
    
    const response = await api.get('/admin/companies/');
    return response.data;
  } catch (error) {
    console.error('Error fetching all companies:', error);
    throw error;
  }
};

// Funzione per ottenere i dettagli di un evento
export const getEventDetails = async (identifier) => {
  try {
    // Usa l'identifier (può essere ID o slug)
    const response = await fetch(`${API_URL}/public/events/${identifier}/`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching event details:', error);
    throw error;
  }
};

// Funzione per ottenere gli eventi preferiti dell'utente
export const getUserLikedEvents = async () => {
  try {
    if (!isAuthenticated()) {
      return [];
    }
    
    // Ottieni l'ID dell'utente corrente
    const currentUser = getCurrentUser();
    const userId = currentUser?.id;
    
    if (!userId) {
      console.warn('ID utente non disponibile, impossibile recuperare gli eventi preferiti');
      return [];
    }
    
    // Usa la chiave specifica per l'utente
    const likedEventsKey = `likedEvents_${userId}`;
    
    // Prova a recuperare gli eventi preferiti dal backend
    try {
      const response = await api.get('/events/liked/');
      console.log('Liked events raw response:', response.data);
      
      // Salva gli ID degli eventi preferiti nel localStorage per coerenza
      const likedEventIds = response.data.map(event => event.id);
      localStorage.setItem(likedEventsKey, JSON.stringify(likedEventIds));
      
      // Verifica che ogni evento abbia tutti i campi necessari
      const completeEvents = await Promise.all(response.data.map(async (event) => {
        // Se l'evento non ha tutti i campi necessari, recupera i dettagli completi
        if (!event.title || !event.description || !event.start_date) {
          try {
            console.log(`Fetching complete details for event ${event.id}`);
            const detailResponse = await api.get(`/events/${event.id}/`);
            return {
              ...event,
              ...detailResponse.data
            };
          } catch (detailError) {
            console.error(`Error fetching details for event ${event.id}:`, detailError);
            // Assicurati che l'evento abbia almeno i campi base
            return {
              ...event,
              title: event.title || `Evento #${event.id}`,
              description: 'Nessuna descrizione disponibile',
              start_date: event.start_date || event.date || new Date().toISOString().split('T')[0]
            };
          }
        }
        return event;
      }));
      
      console.log('Complete liked events:', completeEvents);
      return completeEvents;
    } catch (error) {
      console.error('Error fetching liked events from API:', error);
      
      // Fallback: usa i dati memorizzati localmente
      const likedEventsIds = JSON.parse(localStorage.getItem(likedEventsKey) || '[]');
      
      // Se abbiamo solo gli ID, recuperiamo i dettagli completi degli eventi
      if (likedEventsIds.length > 0) {
        try {
          const eventsPromises = likedEventsIds.map(id => 
            api.get(`/events/${id}/`)
              .then(res => res.data)
              .catch(() => ({ 
                id,
                title: `Evento #${id}`,
                description: 'Informazioni non disponibili',
                start_date: new Date().toISOString().split('T')[0]
              }))
          );
          
          const eventsDetails = await Promise.all(eventsPromises);
          return eventsDetails.filter(event => event && event.id);
        } catch (detailsError) {
          console.error('Error fetching event details:', detailsError);
        }
      }
      
      // Se tutto fallisce, restituisci almeno gli ID con dati minimi
      return likedEventsIds.map(id => ({ 
        id,
        title: `Evento #${id}`,
        description: 'Informazioni non disponibili',
        start_date: new Date().toISOString().split('T')[0]
      }));
    }
  } catch (error) {
    console.error('Error in getUserLikedEvents:', error);
    return [];
  }
};

// Funzione per aggiungere/rimuovere un like a un evento
export const toggleEventLike = async (eventId) => {
  try {
    if (!isAuthenticated()) {
      throw new Error('Utente non autenticato');
    }
    
    // Ottieni l'ID dell'utente corrente
    const currentUser = getCurrentUser();
    const userId = currentUser?.id;
    
    if (!userId) {
      throw new Error('ID utente non disponibile');
    }
    
    // Usa una chiave specifica per l'utente nel localStorage
    const likedEventsKey = `likedEvents_${userId}`;
    const likedEventsString = localStorage.getItem(likedEventsKey);
    let likedEvents = likedEventsString ? JSON.parse(likedEventsString) : [];
    
    // Controlla se l'evento &#xE8; gi&#xE0; nei preferiti
    const eventIndex = likedEvents.indexOf(eventId);
    
    if (eventIndex === -1) {
      // Aggiungi ai preferiti
      likedEvents.push(eventId);
    } else {
      // Rimuovi dai preferiti
      likedEvents.splice(eventIndex, 1);
    }
    
    // Salva la lista aggiornata nel localStorage con la chiave specifica dell'utente
    localStorage.setItem(likedEventsKey, JSON.stringify(likedEvents));
    
    // Invia la richiesta al backend
    try {
      const response = await api.post(`/events/${eventId}/toggle-like/`);
      return response.data;
    } catch (error) {
      console.error('Error updating like status on server:', error);
      // In caso di errore, restituisci comunque lo stato locale aggiornato
      return { success: true, liked: eventIndex === -1 };
    }
  } catch (error) {
    console.error('Error toggling event like:', error);
    throw error;
  }
};

// Esporta l'istanza api per usi generici
export default api;

export const getUserEvents = async () => {
  // Se l'API non &#xE8; ancora implementata sul backend, possiamo restituire un array vuoto
  // o dati di esempio per lo sviluppo
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return [];
    }
    
    // Quando l'API sar&#xE0; disponibile, decommentare questo codice
    // const response = await api.get('user/events/', {
    //   headers: { Authorization: `Token ${token}` }
    // });
    // return response.data;
    
    // Per ora, restituiamo un array vuoto
    console.log('getUserEvents API non ancora implementata, restituisco array vuoto');
    return [];
  } catch (error) {
    console.error('Error fetching user events:', error);
    return [];
  }
};

// Funzione per ottenere il profilo utente
export const getUserProfile = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Utente non autenticato');
    }

    const response = await api.get('/users/profile/', {
      headers: {
        'Authorization': `Token ${token}`
      }
    });

    console.log("Risposta profilo utente:", response.data);
    
    // Assicurati che l'URL dell'avatar sia completo
    const userData = response.data;
    if (userData.avatar) {
      // Se l'URL dell'avatar non &#xE8; completo, aggiungilo
      if (!userData.avatar.startsWith('http')) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8001';
        userData.avatar = userData.avatar.startsWith('/') 
          ? `${baseUrl}${userData.avatar}`
          : `${baseUrl}/${userData.avatar}`;
      }
      console.log("URL avatar completo:", userData.avatar);
    }
    
    // Aggiorna il localStorage con i dati pi&#xF9; recenti dell'utente
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = {
      ...storedUser,
      ...userData
    };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    return userData;
  } catch (error) {
    console.error('Errore durante il recupero del profilo utente:', error);
    throw error;
  }
};

/**
 * Aggiorna il profilo dell'utente
 * @param {FormData} formData - I dati del profilo da aggiornare (username, email, avatar)
 * @returns {Promise<Object>} - I dati aggiornati dell'utente
 */
export const updateUserProfile = async (formData) => {
  try {
    // Assicurati che la richiesta includa il token di autenticazione
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Utente non autenticato');
    }

    // Log per debug
    console.log("Invio richiesta di aggiornamento profilo con FormData:");
    for (let pair of formData.entries()) {
      console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
    }

    // Invia la richiesta con il FormData
    const response = await api.patch('/users/profile/', formData, {
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'multipart/form-data'  // Importante per l'upload di file
      }
    });

    console.log("Risposta dal server:", response.data);

    // Estrai i dati aggiornati
    const updatedData = response.data;

    // Gestisci l'URL dell'avatar
    if (updatedData.avatar) {
      console.log("Avatar ricevuto nella risposta:", updatedData.avatar);
      
      // Assicurati che l'URL dell'avatar sia completo
      if (!updatedData.avatar.startsWith('http')) {
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8001';
        updatedData.avatar = updatedData.avatar.startsWith('/') 
          ? `${baseUrl}${updatedData.avatar}`
          : `${baseUrl}/${updatedData.avatar}`;
      }
      console.log('URL avatar completo dopo aggiornamento:', updatedData.avatar);
    }
    
    // Aggiorna il localStorage con i dati pi&#xF9; recenti
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = {
      ...storedUser,
      ...updatedData
    };
    
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    return updatedData;
  } catch (error) {
    console.error('Errore durante l aggiornamento del profilo:', error);
    throw error;
  }
};

// API functions for carousel
export const getCarouselImages = async () => {
  try {
    const response = await api.get('/carousel/');
    return response.data;
  } catch (error) {
    console.error('Error fetching carousel images:', error);
    throw error;
  }
};

// Aggiungi questa funzione alla fine del file
export const getCompanyProfile = async () => {
  try {
    // Questa &#xE8; semplicemente un alias per getCompanyDetails
    return await getCompanyDetails();
  } catch (error) {
    console.error('Error fetching company profile:', error);
    throw error;
  }
};

export const getCompanyDetails = async () => {
  try {
    const response = await api.get('/company/details/');
    console.log('Company details response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching company details:', error);
    throw error;
  }
};

// Aggiungi questa funzione per ottenere la posizione dell'utente
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    console.log('Richiesta posizione utente...');
    
    // Verifica se la geolocalizzazione &#xE8; supportata
    if (!navigator.geolocation) {
      reject(new Error('La geolocalizzazione non è supportata dal tuo browser.'));
      return;
    }
    
    // Opzioni per la geolocalizzazione
    const options = {
      enableHighAccuracy: true,  // Usa GPS se disponibile
      timeout: 10000,            // Timeout dopo 10 secondi
      maximumAge: 0              // Non usare cache
    };
    
    // Richiedi la posizione
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        
        // Salva nel localStorage
        localStorage.setItem('userLocation', JSON.stringify(userLocation));
        
        console.log('Posizione utente ottenuta:', userLocation);
        resolve(userLocation);
      },
      (error) => {
        console.error('Errore nella geolocalizzazione:', error);
        let errorMessage;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Hai negato il permesso per la geolocalizzazione.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "La posizione non è disponibile.";
            break;
          case error.TIMEOUT:
            errorMessage = "Timeout nella richiesta di posizione.";
            break;
          case error.UNKNOWN_ERROR:
          default:
            errorMessage = "Errore sconosciuto nella geolocalizzazione.";
            break;
        }
        
        reject(new Error(errorMessage));
      },
      options
    );
  });
};

// Funzione per calcolare la distanza tra due punti geografici (formula di Haversine)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Raggio della Terra in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const distance = R * c; // Distanza in km
  return distance;
};

function deg2rad(deg) {
  return deg * (Math.PI/180);
}

/**
 * Verifica la validità di una Partita IVA o Codice Fiscale
 * @param {string} vatNumber - La Partita IVA o il Codice Fiscale da verificare
 * @returns {Promise<Object>} - Oggetto con valid, message e type
 */
export const verifyVatNumber = async (vatNumber) => {
    try {
        console.log('🔍 Verifying VAT/Fiscal Code:', vatNumber);
        
        // Validazione locale del formato
        const isVat = /^\d{11}$/.test(vatNumber); // Partita IVA: 11 cifre
        const isFiscalCode = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(vatNumber); // Codice Fiscale
        
        if (!isVat && !isFiscalCode) {
            return {
                valid: false,
                message: 'Formato non valido. Inserisci una P.IVA (11 cifre) o un Codice Fiscale valido',
                type: null
            };
        }
        
        // Chiama il backend - NOTA: il backend si aspetta 'vatNumber' non 'vat_number'
        const response = await api.post('/company/verify-vat/', {
            vatNumber: vatNumber  // Usa vatNumber come nel backend
        });
        
        console.log('✅ VAT verification response:', response.data);
        return {
            valid: response.data.valid,
            message: response.data.message,
            type: isVat ? 'Partita IVA' : 'Codice Fiscale',
            company_name: response.data.company_name || null
        };
    } catch (error) {
        console.error('❌ Error verifying VAT:', error);
        
        // Se c'è un errore dal backend, prova a usare la validazione locale
        if (error.response && error.response.data) {
            return {
                valid: false,
                message: error.response.data.message || 'Errore durante la verifica',
                type: null
            };
        }
        
        // Fallback alla validazione locale in caso di errore di rete
        const isVat = /^\d{11}$/.test(vatNumber);
        const isFiscalCode = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/i.test(vatNumber);
        
        if (isVat || isFiscalCode) {
            return {
                valid: true,
                message: `${isVat ? 'Partita IVA' : 'Codice Fiscale'} - Formato valido (verifica offline)`,
                type: isVat ? 'Partita IVA' : 'Codice Fiscale',
                company_name: null
            };
        }
        
        return {
            valid: false,
            message: 'Errore durante la verifica. Riprova più tardi',
            type: null
        };
    }
};

/**
 * Aggiorna i dettagli dell'azienda
 * @param {FormData|Object} companyData - I dati dell'azienda da aggiornare
 * @returns {Promise<Object>} - I dati aggiornati dell'azienda
 */
export const updateCompanyDetails = async (companyData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Utente non autenticato');
    }

    // Determina se i dati sono FormData (per file upload) o un oggetto normale
    const isFormData = companyData instanceof FormData;
    
    console.log("Invio richiesta di aggiornamento azienda:", isFormData ? "FormData" : companyData);

    const config = {
      headers: {
        'Authorization': `Token ${token}`,
        // null removes the instance-level 'application/json' default so the browser
        // can set multipart/form-data + boundary automatically when sending FormData
        'Content-Type': isFormData ? null : 'application/json',
      }
    };

    const response = await api.patch('/company/details/', companyData, config);

    console.log("Risposta aggiornamento azienda:", response.data);

    // Aggiorna il localStorage se necessario
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const updatedUser = {
      ...storedUser,
      company: response.data
    };
    
    localStorage.setItem('user', JSON.stringify(updatedUser));

    return response.data;
  } catch (error) {
    console.error('Errore durante l aggiornamento dei dettagli azienda:', error);
    throw error;
  }
};

// Funzioni per il tracking degli eventi
export const trackEventView = async (eventId) => {
  try {
    const response = await axios.post(`${API_URL}/events/${eventId}/track-view/`);
    return response.data;
  } catch (error) {
    console.error('Error tracking event view:', error);
    throw error;
  }
};


export const trackEventClick = async (eventId) => {
  try {
    const response = await api.post(`/events/${eventId}/track-click/`);
    return response.data;
  } catch { /* silent */ }
};

// ── RSVP ──
export const getEventRSVP = async (eventId) => {
  const response = await api.get(`/events/${eventId}/rsvp/`);
  return response.data;
};
export const setEventRSVP = async (eventId, data) => {
  const response = await api.post(`/events/${eventId}/rsvp/`, data);
  return response.data;
};
export const getUserRsvps = async () => {
  const response = await api.get('/user/rsvp/');
  return response.data;
};
export const googleLogin = async (credential, mode = 'auto') => {
  const response = await api.post('/auth/google/', { credential, mode });
  return response.data;
};

// ── Community chat ──
export const getEventMessages = async (eventId) => {
  const response = await api.get(`/events/${eventId}/messages/`);
  return response.data;
};
export const postEventMessage = async (eventId, text) => {
  const response = await api.post(`/events/${eventId}/messages/`, { text });
  return response.data;
};

// ── Match ──
export const getEventMatch = async (eventId) => {
  const response = await api.get(`/events/${eventId}/match/`);
  return response.data;
};
// ── Love Chat ──
export const sendLoveChatRequest = async (receiverId, eventId) => {
  const r = await api.post('/love-chat/', { receiver_id: receiverId, event_id: eventId });
  return r.data;
};
export const getLoveChatRequests = async () => {
  const r = await api.get('/love-chat/');
  return r.data;
};
export const respondLoveChat = async (requestId, action) => {
  const r = await api.post(`/love-chat/${requestId}/respond/`, { action });
  return r.data;
};
export const getLoveChatMessages = async (requestId) => {
  const r = await api.get(`/love-chat/${requestId}/messages/`);
  return r.data;
};
export const sendLoveChatMessage = async (requestId, text) => {
  const r = await api.post(`/love-chat/${requestId}/messages/`, { text });
  return r.data;
};

// ── Tinder Participant Like ──
export const likeParticipant = async (eventId, likedId) => {
  const r = await api.post(`/events/${eventId}/like-participant/`, { liked_id: likedId });
  return r.data;
};
export const getMyParticipantLikes = async (eventId) => {
  const r = await api.get(`/events/${eventId}/my-likes/`);
  return r.data;
};

// ── Stripe ──
export const createStripeCheckout = async (plan) => {
  const r = await api.post('/company/checkout/', { plan });
  return r.data;
};

// ── Table Booking ──
export const createTableBooking = async (eventId, data) => {
  const r = await api.post(`/events/${eventId}/book/`, data);
  return r.data;
};

export const getCompanyBookings = async () => {
  const r = await api.get('/company/bookings/');
  return r.data;
};

export const respondBooking = async (id, action) => {
  const r = await api.post(`/company/bookings/${id}/respond/`, { action });
  return r.data;
};

export const toggleTableBooking = async (eventId, enabled) => {
  const r = await api.patch(`/company/events/${eventId}/`, { table_booking_enabled: enabled });
  return r.data;
};

export const cancelSubscription = async () => {
  const r = await api.post('/company/cancel-subscription/');
  return r.data;
};

export const getCompanyPublic = async (companyId) => {
  const r = await api.get(`/organizzatore/${companyId}/`);
  return r.data;
};

export const requestPasswordReset = async (email) => {
  const r = await api.post('/users/password-reset/', { email });
  return r.data;
};

export const confirmPasswordReset = async (uid, token, new_password) => {
  const r = await api.post('/users/password-reset/confirm/', { token, password: new_password });
  return r.data;
};

// Ticket Marketplace
export const getTicketListings = async (eventId) => {
  const r = await api.get(`/events/${eventId}/tickets/`);
  return r.data;
};

export const createTicketListing = async (eventId, data) => {
  const r = await api.post(`/events/${eventId}/tickets/`, data);
  return r.data;
};

export const buyTicket = async (listingId, quantity = 1) => {
  const r = await api.post(`/tickets/${listingId}/buy/`, { quantity });
  return r.data;
};

export const cancelTicketListing = async (listingId) => {
  const r = await api.delete(`/tickets/${listingId}/cancel/`);
  return r.data;
};

export const getSellerStatus = async () => {
  const r = await api.get('/seller/status/');
  return r.data;
};

export const startSellerOnboarding = async () => {
  const r = await api.post('/seller/onboard/');
  return r.data;
};

export const getMyPurchases = async () => {
  const r = await api.get('/tickets/purchases/');
  return r.data;
};

export const confirmTicketReceipt = async (purchaseId) => {
  const r = await api.post(`/tickets/purchases/${purchaseId}/confirm/`);
  return r.data;
};

export const setBuyerAddress = async (purchaseId, address) => {
  const r = await api.post(`/tickets/purchases/${purchaseId}/address/`, { address });
  return r.data;
};

export const getMyListings = async () => {
  const r = await api.get('/seller/listings/');
  return r.data;
};

export const updateTicketListing = async (listingId, data) => {
  const r = await api.patch(`/seller/listings/${listingId}/update/`, data);
  return r.data;
};

export const updateShipping = async (purchaseId, data) => {
  const r = await api.post(`/seller/purchases/${purchaseId}/ship/`, data);
  return r.data;
};

// ── Storie Evento ──────────────────────────────────────────────────────
export const getEventStories = async (eventId) => {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Token ${token}` } : {};
  const r = await axios.get(`${API_URL}/events/${eventId}/stories/`, { headers, timeout: 10000 });
  return r.data;
};

export const createEventStory = async (eventId, formData) => {
  const r = await api.post(`/events/${eventId}/stories/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
};

export const markStoryViewed = async (storyId) => {
  const r = await api.post(`/stories/${storyId}/view/`);
  return r.data;
};

export const deleteStory = async (storyId) => {
  const r = await api.delete(`/stories/${storyId}/delete/`);
  return r.data;
};

export const getAdminTicketListings = async () => {
  const r = await api.get('/admin/ticket-listings/');
  return r.data;
};
